/**
 * CarrierRateCard - Expandable carrier card with rate editing table
 */

import React, { useState } from 'react';
import {
  Check,
  X,
  Trash2,
  ChevronDown,
  ChevronRight,
  Star,
  StarOff,
  Route,
} from 'lucide-react';
import type { ShippingCarrier, CustomShippingRoute } from '../../types/shipping';

interface CarrierRateCardProps {
  carrier: ShippingCarrier;
  carrierRoutes: CustomShippingRoute[];
  allRoutes: CustomShippingRoute[];
  isExpanded: boolean;
  visibleDesi: number[];
  getRate: (carrierId: string, routeId: string, desi: number) => number | undefined;
  updateRate: (carrierId: string, routeId: string, desi: number, value: number | null) => void;
  isPriorityCarrier: (routeId: string, carrierId: string) => boolean;
  setPriorityCarrier: (routeId: string, carrierId: string | null) => void;
  updateCarrierCurrency: (carrier: ShippingCarrier, newCurrency: string) => void;
  onToggleExpand: () => void;
  onDelete: () => void;
  onShowRoutes: () => void;
}

const CarrierRateCard: React.FC<CarrierRateCardProps> = ({
  carrier,
  carrierRoutes,
  allRoutes,
  isExpanded,
  visibleDesi,
  getRate,
  updateRate,
  isPriorityCarrier,
  setPriorityCarrier,
  updateCarrierCurrency,
  onToggleExpand,
  onDelete,
  onShowRoutes,
}) => {
  // Cell editing state (local to this card)
  const [editingCell, setEditingCell] = useState<{ routeId: string; desi: number } | null>(null);
  const [editValue, setEditValue] = useState('');

  const startEdit = (routeId: string, desi: number, currentValue: number | undefined) => {
    setEditingCell({ routeId, desi });
    setEditValue(currentValue?.toString() || '');
  };

  const saveEdit = () => {
    if (!editingCell) return;
    const newValue = editValue === '' ? null : parseFloat(editValue);
    if (newValue !== null && isNaN(newValue)) return;
    updateRate(carrier.id, editingCell.routeId, editingCell.desi, newValue);
    setEditingCell(null);
  };

  const cancelEdit = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') saveEdit();
    else if (e.key === 'Escape') cancelEdit();
  };

  const renderCell = (routeId: string, desi: number) => {
    const isEditing = editingCell?.routeId === routeId && editingCell?.desi === desi;
    const value = getRate(carrier.id, routeId, desi);

    if (isEditing) {
      return (
        <div className="flex items-center gap-1">
          <input
            type="number"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className="w-16 px-1 py-0.5 text-xs border border-indigo-400 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500"
            autoFocus
            step="0.01"
          />
          <button onClick={saveEdit} className="p-0.5 text-green-600 hover:bg-green-50 rounded">
            <Check className="w-3 h-3" />
          </button>
          <button onClick={cancelEdit} className="p-0.5 text-red-600 hover:bg-red-50 rounded">
            <X className="w-3 h-3" />
          </button>
        </div>
      );
    }

    return (
      <button
        onClick={() => startEdit(routeId, desi, value)}
        className="w-full h-full px-2 py-1 text-right hover:bg-indigo-50 rounded transition-colors text-xs"
      >
        {value !== undefined ? value.toFixed(2) : '-'}
      </button>
    );
  };

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      {/* Carrier Header */}
      <div
        className="flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors cursor-pointer"
        onClick={onToggleExpand}
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {isExpanded ? (
            <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
          ) : (
            <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
          )}
          <div
            className="w-4 h-4 rounded-full flex-shrink-0"
            style={{ backgroundColor: carrier.color }}
          />
          <span className="font-semibold text-slate-800">{carrier.name}</span>
          <span className="text-sm text-slate-500">({carrier.shortName})</span>
          <select
            value={carrier.currency || 'USD'}
            onChange={(e) => {
              e.stopPropagation();
              updateCarrierCurrency(carrier, e.target.value);
            }}
            onClick={(e) => e.stopPropagation()}
            className={`text-xs px-1.5 py-0.5 rounded font-medium cursor-pointer border-0 ${
              carrier.currency === 'TRY' ? 'bg-red-100 text-red-700' :
              carrier.currency === 'EUR' ? 'bg-blue-100 text-blue-700' :
              carrier.currency === 'GBP' ? 'bg-purple-100 text-purple-700' :
              'bg-green-100 text-green-700'
            }`}
            title="Para birimini de\u011Fi\u015Ftir"
          >
            <option value="USD">$ USD</option>
            <option value="EUR">{'\u20AC'} EUR</option>
            <option value="TRY">{'\u20BA'} TRY</option>
            <option value="GBP">{'\u00A3'} GBP</option>
          </select>

          {/* Route flags */}
          <div className="flex items-center gap-1 ml-2">
            {carrierRoutes.slice(0, 6).map(route => (
              <span key={route.routeId} className="text-sm" title={route.name}>
                {route.flag}
              </span>
            ))}
            {carrierRoutes.length > 6 && (
              <span className="text-xs text-slate-400">+{carrierRoutes.length - 6}</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0" onClick={e => e.stopPropagation()}>
          <span className="text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">
            {carrierRoutes.length}/{allRoutes.length}
          </span>
          <button
            onClick={onShowRoutes}
            className="p-1.5 text-slate-400 hover:text-emerald-500 hover:bg-emerald-50 rounded transition-colors"
            title="Rotalar\u0131 d\u00FCzenle"
          >
            <Route className="w-4 h-4" />
          </button>
          <button
            onClick={onDelete}
            className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
            title="Firmay\u0131 sil"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Rate Table */}
      {isExpanded && carrierRoutes.length > 0 && (
        <div className="border-t border-slate-200 flex">
          <div className="overflow-x-auto flex-shrink-0">
            <table className="text-sm">
              <thead>
                <tr className="bg-slate-50">
                  <th className="sticky left-0 bg-slate-50 px-4 py-2 text-left font-semibold text-slate-700 min-w-[80px] z-10">
                    DESI
                  </th>
                  {carrierRoutes.map(route => {
                    const isPriority = isPriorityCarrier(route.routeId, carrier.id);
                    return (
                      <th key={route.routeId} className={`px-2 py-2 text-center ${carrierRoutes.length <= 3 ? 'min-w-[120px]' : 'min-w-[85px]'}`}>
                        <div className="flex items-center justify-center gap-1">
                          <span>{route.flag}</span>
                          <span className="font-semibold text-slate-700 text-xs">{route.shortName}</span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setPriorityCarrier(route.routeId, isPriority ? null : carrier.id);
                            }}
                            className={`p-0.5 rounded ${
                              isPriority ? 'text-amber-500' : 'text-slate-300 hover:text-slate-400'
                            }`}
                            title={isPriority ? '\u00D6ncelikli firma (t\u0131kla kald\u0131rmak i\u00E7in)' : '\u00D6ncelikli yap'}
                          >
                            {isPriority ? <Star className="w-3 h-3 fill-current" /> : <StarOff className="w-3 h-3" />}
                          </button>
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {visibleDesi.map((desi, idx) => (
                  <tr
                    key={desi}
                    className={`border-t border-slate-100 ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}
                  >
                    <td className="sticky left-0 bg-inherit px-4 py-1 font-medium text-slate-700 z-10">
                      {desi}
                    </td>
                    {carrierRoutes.map(route => (
                      <td key={route.routeId} className="px-1 py-1 text-center">
                        {renderCell(route.routeId, desi)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Info panel when few routes */}
          {carrierRoutes.length <= 3 && (
            <div className="flex-1 bg-slate-50/50 border-l border-slate-200 p-4 flex flex-col justify-center items-center text-center min-h-[200px]">
              <div className="space-y-3">
                <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto">
                  <Route className="w-6 h-6 text-slate-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-600">
                    {carrierRoutes.length === 1 ? 'Tek rota aktif' : `${carrierRoutes.length} rota aktif`}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    {allRoutes.length - carrierRoutes.length} rota daha eklenebilir
                  </p>
                </div>
                <button
                  onClick={onShowRoutes}
                  className="text-xs text-emerald-600 hover:text-emerald-700 font-medium"
                >
                  Daha fazla rota ekle {'\u2192'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Empty state when no routes */}
      {isExpanded && carrierRoutes.length === 0 && (
        <div className="border-t border-slate-200 px-4 py-8 text-center text-slate-500">
          <Route className="w-8 h-8 mx-auto mb-2 text-slate-300" />
          <p className="text-sm">Bu firma i{'\u00E7'}in rota se{'\u00E7'}ilmedi</p>
          <button
            onClick={onShowRoutes}
            className="mt-2 text-sm text-emerald-600 hover:text-emerald-700"
          >
            Rota ekle {'\u2192'}
          </button>
        </div>
      )}
    </div>
  );
};

export default CarrierRateCard;
